import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Drawer,
  Group,
  Modal,
  NumberInput,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ADMIN_LIST_PAGE_SIZE,
  adminErrText,
  adminGet,
  adminSend,
  AdminApiError,
} from "../../api.ts";
import { AdminTable } from "../../components/AdminTable.tsx";

interface UserItem {
  id: string;
  tgUserId: number;
  tgUsername: string | null;
  tgFirstName: string | null;
  tgLanguage: string | null;
  spins: number;
  totalSpins: number;
  totalPrizes: number;
  banReason: string | null;
  lastActiveAt: string | null;
}

interface UserDetail {
  user: UserItem & {
    bannedAt: string | null;
    preferredLanguage: string | null;
  };
  history: Array<{
    id: string;
    prizeName: string;
    prizeType: "thanks" | "extra_spins" | "physical" | "virtual";
    requiresShipping: boolean;
    shippingConfirmed: boolean;
    isJackpot: boolean;
    createdAt: string;
  }>;
  shipping: null | {
    recipientName: string;
    phoneMasked: string;
    state: string;
    city: string;
    postalCode: string;
    addressLine1Masked: string | null;
    addressLine2Masked: string | null;
  };
}

interface ShippingFull {
  spinId: string;
  confirmedAt: string;
  address: {
    recipientName: string;
    phone: string;
    country: string;
    state: string;
    city: string;
    postalCode: string;
    addressLine1: string;
    addressLine2: string | null;
    note: string | null;
  };
}

export function UsersTab(props: { readonly botId: string }) {
  const { botId } = props;
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  // 防抖：搜索框逐字输入不应每次按键都打后端，仅停顿 300ms 后才查询。
  const [debouncedQuery] = useDebouncedValue(query, 300);
  const [banFilter, setBanFilter] = useState<"all" | "normal" | "banned">("all");
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const [grantOpen, setGrantOpen] = useState<UserItem | null>(null);
  const [shippingData, setShippingData] = useState<ShippingFull | null>(null);
  const [extraUsers, setExtraUsers] = useState<UserItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const grantForm = useForm<{ delta: number; reason: string }>({
    initialValues: { delta: 0, reason: "" },
    validate: {
      delta: () => null,
      reason: (v) => (v.trim().length === 0 ? "必填" : null),
    },
  });

  const list = useQuery({
    queryKey: ["bot", botId, "users", debouncedQuery],
    queryFn: () =>
      adminGet<{ items: UserItem[]; nextCursor: string | null }>(
        `/bots/${botId}/users?query=${encodeURIComponent(debouncedQuery)}&limit=${ADMIN_LIST_PAGE_SIZE}`,
      ),
    refetchInterval: 60_000,
  });

  // 搜索词改变时清空翻页缓存
  useEffect(() => {
    setExtraUsers([]);
    setNextCursor(null);
  }, [debouncedQuery]);

  const loadMoreUsers = useMutation({
    mutationFn: (cursor: string) =>
      adminGet<{ items: UserItem[]; nextCursor: string | null }>(
        `/bots/${botId}/users?query=${encodeURIComponent(debouncedQuery)}&limit=${ADMIN_LIST_PAGE_SIZE}&cursor=${encodeURIComponent(cursor)}`,
      ),
    onSuccess: (res) => {
      setExtraUsers((prev) => [...prev, ...res.items]);
      setNextCursor(res.nextCursor);
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });
  const detail = useQuery({
    queryKey: ["bot", botId, "user-detail", detailUserId],
    queryFn: () => adminGet<UserDetail>(`/bots/${botId}/users/${detailUserId}`),
    enabled: detailUserId !== null,
  });
  const grant = useMutation({
    mutationFn: (v: { delta: number; reason: string }) =>
      adminSend("POST", `/bots/${botId}/users/${grantOpen?.id}/grant-spins`, v),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已发放" });
      setGrantOpen(null);
      grantForm.reset();
      void qc.invalidateQueries({ queryKey: ["bot", botId, "users"] });
      if (detailUserId !== null) {
        void qc.invalidateQueries({ queryKey: ["bot", botId, "user-detail", detailUserId] });
      }
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });
  const ban = useMutation({
    mutationFn: (args: { id: string; banReason: string | null }) =>
      adminSend("PATCH", `/bots/${botId}/users/${args.id}`, { banReason: args.banReason }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已更新" });
      void qc.invalidateQueries({ queryKey: ["bot", botId, "users"] });
      if (detailUserId !== null) {
        void qc.invalidateQueries({ queryKey: ["bot", botId, "user-detail", detailUserId] });
      }
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });
  const decryptShipping = useMutation({
    mutationFn: (spinId: string) =>
      adminGet<ShippingFull>(`/bots/${botId}/spins/${spinId}/shipping-full`),
    onSuccess: (data) => setShippingData(data),
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  const allUsers = useMemo(
    () => [...(list.data?.items ?? []), ...extraUsers],
    [list.data?.items, extraUsers],
  );
  // 封禁筛选仅作用于已加载页（API 无 banReason 参数）
  const visibleUsers = useMemo(() => {
    if (banFilter === "all") return allUsers;
    if (banFilter === "normal") return allUsers.filter((u) => u.banReason === null);
    return allUsers.filter((u) => u.banReason !== null);
  }, [allUsers, banFilter]);
  const curCursor = extraUsers.length > 0 ? nextCursor : (list.data?.nextCursor ?? null);

  const renderUserRow = (row: UserItem) => (
    <Table.Tr key={row.id}>
      <Table.Td>{row.tgUserId}</Table.Td>
      <Table.Td>{row.tgUsername ?? "-"}</Table.Td>
      <Table.Td>{row.spins}</Table.Td>
      <Table.Td>{row.totalSpins}</Table.Td>
      <Table.Td>{row.totalPrizes}</Table.Td>
      <Table.Td>{row.lastActiveAt ?? "-"}</Table.Td>
      <Table.Td>
        {row.banReason === null ? (
          <Badge color="green">正常</Badge>
        ) : (
          <Badge color="red">{row.banReason}</Badge>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap={4}>
          <Button size="xs" variant="outline" onClick={() => setDetailUserId(row.id)}>
            详情
          </Button>
          <Button
            size="xs"
            variant="outline"
            onClick={() => {
              grantForm.reset();
              setGrantOpen(row);
            }}
          >
            加 / 减 Spins
          </Button>
          {row.banReason === null ? (
            <Button
              size="xs"
              color="red"
              variant="outline"
              onClick={() => {
                modals.openConfirmModal({
                  title: "确认封禁",
                  children: `确认封禁用户 ${row.tgUserId}？封禁后该用户将无法继续使用 Bot。`,
                  labels: { confirm: "封禁", cancel: "取消" },
                  confirmProps: { color: "red" },
                  onConfirm: () => ban.mutate({ id: row.id, banReason: "manual" }),
                });
              }}
            >
              封禁
            </Button>
          ) : (
            <Button
              size="xs"
              variant="outline"
              onClick={() => {
                modals.openConfirmModal({
                  title: "确认解禁",
                  children: `确认解禁用户 ${row.tgUserId}？解禁后该用户将恢复正常访问。`,
                  labels: { confirm: "解禁", cancel: "取消" },
                  onConfirm: () => ban.mutate({ id: row.id, banReason: null }),
                });
              }}
            >
              解禁
            </Button>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );

  const userRows = visibleUsers.map(renderUserRow);

  return (
    <>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md" wrap="wrap" gap="sm">
          <Text fw={600} size="lg">
            用户
          </Text>
          <Group gap="sm" wrap="wrap">
            <Select
              size="xs"
              data={[
                { label: "全部", value: "all" },
                { label: "正常", value: "normal" },
                { label: "已封禁", value: "banned" },
              ]}
              value={banFilter}
              onChange={(v) => setBanFilter((v ?? "all") as "all" | "normal" | "banned")}
              style={{ width: 120 }}
              clearable={false}
            />
            <TextInput
              placeholder="搜索 TG ID 或用户名"
              style={{ width: 280 }}
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
            />
          </Group>
        </Group>
        {list.isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} h={40} radius="sm" />
            ))}
          </Stack>
        ) : (
          <Stack gap="xs">
            <AdminTable minWidth={700}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>TG ID</Table.Th>
                  <Table.Th>用户名</Table.Th>
                  <Table.Th>Spins</Table.Th>
                  <Table.Th>累计抽奖</Table.Th>
                  <Table.Th>累计获奖</Table.Th>
                  <Table.Th>最近活跃</Table.Th>
                  <Table.Th>状态</Table.Th>
                  <Table.Th>操作</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {userRows.length > 0 ? (
                  userRows
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={8} style={{ textAlign: "center" }}>
                      <Text c="dimmed" py="md">
                        {debouncedQuery.trim().length > 0 ? "未找到匹配用户" : "暂无用户"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </AdminTable>
            {curCursor !== null && (
              <Group justify="center">
                <Button
                  variant="outline"
                  size="sm"
                  loading={loadMoreUsers.isPending}
                  onClick={() => loadMoreUsers.mutate(curCursor)}
                >
                  加载更多
                </Button>
              </Group>
            )}
          </Stack>
        )}
      </Card>

      <Modal
        title={`加 / 减 Spins：${grantOpen?.tgUserId ?? ""}`}
        opened={grantOpen !== null}
        onClose={() => setGrantOpen(null)}
        keepMounted={false}
      >
        <form
          onSubmit={grantForm.onSubmit((v) =>
            grant.mutate({ delta: Number(v.delta), reason: v.reason }),
          )}
        >
          <Stack gap="sm">
            <NumberInput label="变化量（可为负）" required {...grantForm.getInputProps("delta")} />
            <TextInput
              label="原因"
              placeholder="客服补偿 / 活动奖励"
              required
              {...grantForm.getInputProps("reason")}
            />
            <Button type="submit" loading={grant.isPending}>
              提交
            </Button>
          </Stack>
        </form>
      </Modal>

      <Drawer
        title="用户详情"
        opened={detailUserId !== null}
        onClose={() => setDetailUserId(null)}
        size="lg"
        position="right"
      >
        {detail.data && (
          <Stack gap="md">
            <Table>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Th>ID</Table.Th>
                  <Table.Td>{detail.data.user.id}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>TG ID</Table.Th>
                  <Table.Td>{detail.data.user.tgUserId}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>用户名</Table.Th>
                  <Table.Td>{detail.data.user.tgUsername ?? "-"}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>TG 名字</Table.Th>
                  <Table.Td>{detail.data.user.tgFirstName ?? "-"}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>语言</Table.Th>
                  <Table.Td>{detail.data.user.tgLanguage ?? "-"}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>Spins</Table.Th>
                  <Table.Td>{detail.data.user.spins}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>累计抽奖</Table.Th>
                  <Table.Td>{detail.data.user.totalSpins}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>封禁原因</Table.Th>
                  <Table.Td>{detail.data.user.banReason ?? "-"}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>偏好语言</Table.Th>
                  <Table.Td>{detail.data.user.preferredLanguage ?? "-"}</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
            {detail.data.shipping !== null && (
              <Card shadow="xs" padding="sm" radius="sm" withBorder>
                <Text fw={600} size="sm" mb="xs">
                  收件地址（脱敏）
                </Text>
                <Table>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Th>收件人</Table.Th>
                      <Table.Td>{detail.data.shipping.recipientName}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Th>电话</Table.Th>
                      <Table.Td>{detail.data.shipping.phoneMasked}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Th>州 / 城市</Table.Th>
                      <Table.Td>
                        {detail.data.shipping.state} / {detail.data.shipping.city}
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Th>PIN 码</Table.Th>
                      <Table.Td>{detail.data.shipping.postalCode}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Th>详细地址</Table.Th>
                      <Table.Td>{detail.data.shipping.addressLine1Masked ?? "-"}</Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Card>
            )}
            <Card shadow="xs" padding="sm" radius="sm" withBorder>
              <Text fw={600} size="sm" mb="xs">
                最近抽奖（50 条）
              </Text>
              <AdminTable minWidth={400}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>时间</Table.Th>
                    <Table.Th>奖品</Table.Th>
                    <Table.Th>类型</Table.Th>
                    <Table.Th>实物</Table.Th>
                    <Table.Th>操作</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {detail.data.history.map((h) => (
                    <Table.Tr key={h.id}>
                      <Table.Td>{h.createdAt}</Table.Td>
                      <Table.Td>{h.prizeName}</Table.Td>
                      <Table.Td>
                        <Badge size="xs" color="gray">
                          {h.prizeType}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {h.requiresShipping ? (
                          h.shippingConfirmed ? (
                            <Badge size="xs" color="green">
                              已锁地址
                            </Badge>
                          ) : (
                            <Badge size="xs" color="orange">
                              待填地址
                            </Badge>
                          )
                        ) : (
                          "-"
                        )}
                      </Table.Td>
                      <Table.Td>
                        {h.prizeType === "physical" && h.shippingConfirmed ? (
                          <Button
                            size="xs"
                            variant="outline"
                            loading={decryptShipping.isPending}
                            onClick={() => {
                              modals.openConfirmModal({
                                title: "查看完整地址",
                                children: "查看该记录将留痕到 admin_audit 日志，确认继续？",
                                labels: { confirm: "确认查看", cancel: "取消" },
                                onConfirm: () => decryptShipping.mutate(h.id),
                              });
                            }}
                          >
                            查看完整地址
                          </Button>
                        ) : null}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </AdminTable>
            </Card>
          </Stack>
        )}
      </Drawer>

      <Modal
        title="完整收件地址（操作已留痕）"
        opened={shippingData !== null}
        onClose={() => setShippingData(null)}
        size="md"
      >
        {shippingData !== null && (
          <Table>
            <Table.Tbody>
              <Table.Tr>
                <Table.Th>收件人</Table.Th>
                <Table.Td>{shippingData.address.recipientName}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>电话</Table.Th>
                <Table.Td>{shippingData.address.phone}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>国家</Table.Th>
                <Table.Td>{shippingData.address.country}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>邦 / 州</Table.Th>
                <Table.Td>{shippingData.address.state}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>城市</Table.Th>
                <Table.Td>{shippingData.address.city}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>PIN 码</Table.Th>
                <Table.Td>{shippingData.address.postalCode}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>详细地址 1</Table.Th>
                <Table.Td>{shippingData.address.addressLine1}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>详细地址 2</Table.Th>
                <Table.Td>{shippingData.address.addressLine2 ?? "-"}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>备注</Table.Th>
                <Table.Td>{shippingData.address.note ?? "-"}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>锁定时间</Table.Th>
                <Table.Td>{shippingData.confirmedAt}</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        )}
      </Modal>
    </>
  );
}
